import { theme } from 'common/theme/app';
import Seo from 'components/seo';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';
import React, { useState } from 'react';
import { PiClock, PiEnvelopeSimple } from 'react-icons/pi';
import styled from 'styled-components';

const ContactWrapper = styled.div`
  width: 100%;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h3 {
    font-size: 20px;
    margin-bottom: 20px;
    font-weight: 600;
  }
  
  p {
    text-align: left;
    margin-bottom: 15px;
  }
  
  .info-item {
    display: flex;
    margin-bottom: 24px;
    align-items: flex-start;
  }
  
  .icon {
    margin-right: 15px;
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    flex: 0 0 42px;
    border-radius: 50%;
    color: ${theme.colors.headingColor};
    background: ${theme.colors.secondary};
    font-size: 21px;
  }
`;

const ContactForm = styled.div`
  form {
    display: flex;
    flex-direction: column;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px 15px;
    border: 1px solid ${theme.colors.outline};
    border-radius: 14px;
    background: ${theme.colors.surfaceRaised};
    font-size: 16px;
    transition: border-color 0.3s ease;
    
    &:focus {
      border-color: ${theme.colors.primary};
      outline: 3px solid ${theme.colors.focusRing};
      outline-offset: 2px;
    }
  }

  select {
    appearance: none;
    background-image: linear-gradient(45deg, transparent 50%, ${theme.colors.headingColor} 50%),
      linear-gradient(135deg, ${theme.colors.headingColor} 50%, transparent 50%);
    background-position: calc(100% - 20px) calc(50% - 3px), calc(100% - 14px) calc(50% - 3px);
    background-size: 6px 6px, 6px 6px;
    background-repeat: no-repeat;
    padding-right: 40px;
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
  
  button {
    background-color: ${theme.colors.primary};
    color: white;
    border: none;
    padding: 12px 25px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 999px;
    cursor: pointer;
    align-self: flex-start;
    transition: background-color 0.3s ease;
    
    &:hover {
      background-color: ${theme.colors.primaryHover};
    }

    &:focus-visible {
      outline: 3px solid ${theme.colors.focusRing};
      outline-offset: 2px;
    }
  }
  
  .form-success {
    padding: 15px;
    background-color: #e7f7ed;
    color: #1e8449;
    border-radius: 12px;
    margin-bottom: 20px;
  }

  .form-error {
    padding: 15px;
    background-color: #fdecea;
    color: #b71c1c;
    border-radius: 12px;
    margin-bottom: 20px;
  }

  button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Contact = () => {
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    if (!formData.get('form-name')) {
      formData.set('form-name', 'contact');
    }
    const topic = String(formData.get('topic') || 'General');
    formData.set('subject', `Furbuz Contact: ${topic}`);
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
      });
      if (!response.ok) {
        throw new Error(`Netlify returned ${response.status}`);
      }
      form.reset();
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        'We could not send your message right now. Please email support@furbuz.com instead.'
      );
    }
  };

  return (
    <SiteLayout>
        <Seo title="Contact Us | Furbuz Pet Care App" />
        <ContentPage
          title="Contact Us"
          description="Have questions, feedback, or need assistance with Furbuz? Our team is here to help."
          eyebrow="Support"
          width="1000px"
        >
          <ContactWrapper>
            <ContactGrid>
              <ContactInfo>
                <h3>Get In Touch</h3>
                <div className="info-item">
                  <span className="icon" aria-hidden="true">
                    <PiEnvelopeSimple />
                  </span>
                  <p>
                    <strong>Email us</strong><br />
                    <a href="mailto:support@furbuz.com">support@furbuz.com</a>
                  </p>
                </div>
                
                <div className="info-item">
                  <span className="icon" aria-hidden="true">
                    <PiClock />
                  </span>
                  <p>
                    <strong>Response time</strong><br />
                    We usually reply within 1–2 business days.
                  </p>
                </div>
              </ContactInfo>
              
              <ContactForm>
                {status === 'success' ? (
                  <div className="form-success" role="status">
                    Thank you for your message! We'll get back to you as soon as possible.
                  </div>
                ) : null}
                {status === 'error' ? (
                  <div className="form-error" role="alert">
                    {errorMessage}
                  </div>
                ) : null}
                
                <form 
                  name="contact" 
                  method="POST" 
                  action="/"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p style={{ display: 'none' }}>
                    <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                  </p>
                  
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input type="text" id="name" name="name" required />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select id="subject" name="topic" required defaultValue="">
                      <option value="" disabled>
                        Select a subject
                      </option>
                      <option value="General">General</option>
                      <option value="App issue">App issue</option>
                      <option value="Billing">Billing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" name="message" required></textarea>
                  </div>
                  
                  <button type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              </ContactForm>
            </ContactGrid>
          </ContactWrapper>
        </ContentPage>
    </SiteLayout>
  );
};

export default Contact; 